import { useEffect, useState } from "react";
import {
  Table,
  Card,
  Button,
  message,
  Layout,
  Input,
  Space,
  Modal,
  Form,
  DatePicker,
  Switch,
  Tabs,
  Tag,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import api from "../api/api";
import Sidebar from "../layout/sidebar";
import useAuth from "../utils/useAuth";
import CargaUsuariosCsv from "./UsuariosCSV";

const { Content } = Layout;
const { TabPane } = Tabs;

export default function Usuarios() {
  useAuth();

  const [usuarios, setUsuarios] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const [emailBusqueda, setEmailBusqueda] = useState("");

  const [form] = Form.useForm();
  const [formPerm] = Form.useForm();

  const navigate = useNavigate();

  const isSuccess = (res) => {
    return res?.data?.success === "edited";
  };

  useEffect(() => {
    fetchUsuarios();
    fetchEbooks();
  }, []);

  // 🔌 GET LISTADO
  const fetchUsuarios = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/GetKotobeeUsers");
      setUsuarios(res.data);
    } catch {
      message.error("Error cargando usuarios");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 GET EBOOKS
  const fetchEbooks = async () => {
    try {
      const res = await api.get("/api/GetEbooks");
      setEbooks(res.data);
    } catch {
      message.error("Error cargando ebooks");
    }
  };

  // 🔍 BUSCAR POR EMAIL (NUEVO)
  const handleBuscarPorEmail = async () => {
    if (!emailBusqueda) {
      return message.warning("Ingrese un correo");
    }

    try {
      setLoading(true);

      const res = await api.get(
        `/api/GetKotobeeUserByEmail?email=${emailBusqueda}`,
      );

      let data = res.data;

      // 🔥 NORMALIZACIÓN
      const permsConNorma = data.perms.map((perm) => {
        const ebook = ebooks.find((e) => e.idNormaProveedor === perm.bid);

        return {
          ...perm,
          normaEbook: ebook?.normaEbook || "No encontrado",
          accessexpirationdate: perm.date, // alias
        };
      });

      data.perms = permsConNorma;

      setDetalle(data);
      setModalVisible(true);
    } catch (error) {
      console.error(error);
      message.error("Usuario no encontrado");
    } finally {
      setLoading(false);
    }
  };

  // 🔍 DETALLE DESDE TABLA
  const handleVerDetalle = async (email) => {
    try {
      const res = await api.get(`/api/GetKotobeeUserByEmail?email=${email}`);
      let data = res.data;

      const permsConNorma = data.perms.map((perm) => {
        const ebook = ebooks.find((e) => e.idNormaProveedor === perm.bid);

        return {
          ...perm,
          normaEbook: ebook?.normaEbook || "No encontrado",
          accessexpirationdate: perm.date,
        };
      });

      data.perms = permsConNorma;

      setDetalle(data);
      setModalVisible(true);
    } catch {
      message.error("Error cargando detalle");
    }
  };

  // 🧠 CARGAR FORM
  useEffect(() => {
    if (detalle) {
      form.setFieldsValue({
        email: detalle.email,
        active: detalle.info.active === 1,
      });
    }
  }, [detalle]);

  // 💾 GUARDAR USUARIO
  const handleGuardarUsuario = async () => {
    try {
      const values = await form.validateFields();

      const res = await api.post("/api/EditKotobeeUser", {
        email: values.email,
        active: values.active ? 1 : 0,
      });

      if (isSuccess(res)) {
        message.success("Usuario actualizado");
        fetchUsuarios();
        handleVerDetalle(values.email);
      }
    } catch {
      message.error("Error al actualizar usuario");
    }
  };

  // 💾 GUARDAR PERMISO
  const handleGuardarPermiso = async () => {
    try {
      const values = await formPerm.validateFields();

      const res = await api.post("/api/EditKotobeeUser", {
        email: detalle.email,
        bid: JSON.stringify(values.bid),
        accessexpirationdate: values.accessexpirationdate.format("YYYY-MM-DD"),
      });

      if (isSuccess(res)) {
        message.success("Permiso actualizado");
        formPerm.resetFields();
        handleVerDetalle(detalle.email);
      }
    } catch {
      message.error("Error al actualizar permiso");
    }
  };

  // 📊 TABLA PRINCIPAL
  const columns = [
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Nombre",
      dataIndex: "name",
    },
    {
      title: "Estado",
      dataIndex: "active",
      render: (val) =>
        val === 1 ? (
          <Tag color="green">Activo</Tag>
        ) : (
          <Tag color="red">Inactivo</Tag>
        ),
    },
    {
      title: "Acciones",
      render: (_, record) => (
        <Button
          onClick={() => {
            setEmailBusqueda(record.email);
            handleVerDetalle(record.email);
          }}
        >
          Ver / Editar
        </Button>
      ),
    },
  ];

  // 📚 PERMISOS
  const columnsPerms = [
    {
      title: "Norma",
      dataIndex: "normaEbook",
    },
    {
      title: "Book ID",
      dataIndex: "bid",
    },
    {
      title: "Fecha",
      dataIndex: "accessexpirationdate",
      render: (d) => (d ? new Date(d).toLocaleDateString() : "Sin fecha"),
    },
    {
      title: "Editar",
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => {
            formPerm.setFieldsValue({
              bid: record.bid,
              accessexpirationdate: record.accessexpirationdate
                ? dayjs(record.accessexpirationdate)
                : null,
            });
          }}
        >
          Editar
        </Button>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sidebar />

      <Layout style={{ marginLeft: 200, padding: 20 }}>
        <CargaUsuariosCsv />

        <Content>
          <Card title="Usuarios Kotobee">
            {/* 🔍 BUSCADOR */}
            <Space style={{ marginBottom: 16 }}>
              <Input
                placeholder="Consultar por correo"
                value={emailBusqueda}
                onChange={(e) => setEmailBusqueda(e.target.value)}
                onPressEnter={handleBuscarPorEmail}
                style={{ width: 300 }}
              />

              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleBuscarPorEmail}
              >
                Buscar
              </Button>
            </Space>

            <Table
              dataSource={usuarios}
              columns={columns}
              rowKey="id"
              loading={loading}
            />
          </Card>
        </Content>
      </Layout>

      {/* 🪟 MODAL */}
      <Modal
        title="Gestión de Usuario"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={900}
      >
        <Tabs defaultActiveKey="1">
          <TabPane tab="Información" key="1">
            <Form form={form} layout="vertical">
              <Form.Item name="email" label="Email">
                <Input disabled />
              </Form.Item>

              <Form.Item name="active" label="Activo" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Button type="primary" onClick={handleGuardarUsuario}>
                Guardar Usuario
              </Button>
            </Form>

            <br />

            {detalle && (
              <>
                <p>
                  <b>Último login:</b> {detalle.info.lastlogin}
                </p>
                <p>
                  <b>Logins exitosos:</b> {detalle.info.successlogins}
                </p>
                <p>
                  <b>Logins fallidos:</b> {detalle.info.faillogins}
                </p>
              </>
            )}
          </TabPane>

          <TabPane tab="Permisos" key="2">
            <Table
              dataSource={detalle?.perms || []}
              columns={columnsPerms}
              rowKey="id"
              pagination={false}
            />

            <br />

            <Form form={formPerm} layout="vertical">
              <Form.Item name="bid" label="Book ID">
                <Input />
              </Form.Item>

              <Form.Item name="accessexpirationdate" label="Fecha Expiración">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>

              <Button type="primary" onClick={handleGuardarPermiso}>
                Guardar Permiso
              </Button>
            </Form>
          </TabPane>

          <TabPane tab="Acceso" key="3">
            <p>
              Estado de acceso: <b>{detalle?.bookAccess?.access}</b>
            </p>
          </TabPane>
        </Tabs>
      </Modal>
    </Layout>
  );
}
